// Lógica pura de la selección múltiple de tablas de guías (rango con Shift y
// arrastre). Vive acá y no dentro del componente para poder testearla: la
// pantalla está detrás de login y no hay forma de ejercitarla de otro modo.

interface Identifiable {
  shipmentHeaderID: string
}

const byShipmentHeaderID = (t: any): string => t.shipmentHeaderID

/**
 * SOBRECARGAS, no un parámetro con default.
 *
 * La tentación era `key: (t: T) => string = (t: any) => t.shipmentHeaderID` para
 * que sirviera con cualquier tipo. Pero eso cambia un error de compilación por un
 * fallo SILENCIOSO: un tipo sin `shipmentHeaderID` devuelve `undefined` en todas
 * las claves, y entonces `toggleSelection` trata todas las filas como si fueran
 * la misma.
 *
 * Justo el tipo de bug que estas funciones vienen a cerrar. Con dos sobrecargas,
 * los llamadores que ya existen (que pasan objetos con `shipmentHeaderID`) siguen
 * compilando sin tocarlos, y usar un tipo nuevo sin pasar extractor vuelve a ser
 * un error de tipos.
 */

/**
 * Marca o desmarca un lote sobre la selección actual, comparando por clave y no
 * por referencia de objeto.
 *
 * Al marcar, los elementos del lote se reinsertan al final: así un item que ya
 * estaba seleccionado no queda duplicado.
 */
export function mergeSelection<T extends Identifiable>(
  current: T[],
  targets: T[],
  selected: boolean
): T[]
export function mergeSelection<T>(
  current: T[],
  targets: T[],
  selected: boolean,
  key: (t: T) => string
): T[]
export function mergeSelection<T>(
  current: T[],
  targets: T[],
  selected: boolean,
  key: (t: T) => string = byShipmentHeaderID
): T[] {
  const ids = new Set(targets.map(key))
  const rest = current.filter((s) => !ids.has(key(s)))

  return selected ? [...rest, ...targets] : rest
}

/** Alterna un solo item, comparando por clave. */
export function toggleSelection<T extends Identifiable>(
  current: T[],
  target: T
): T[]
export function toggleSelection<T>(
  current: T[],
  target: T,
  key: (t: T) => string
): T[]
export function toggleSelection<T>(
  current: T[],
  target: T,
  key: (t: T) => string = byShipmentHeaderID
): T[] {
  const targetKey = key(target)

  return current.some((s) => key(s) === targetKey)
    ? current.filter((s) => key(s) !== targetKey)
    : [...current, target]
}

/**
 * Tramo contiguo entre dos índices, en cualquier orden (el usuario puede armar
 * el rango hacia abajo o hacia arriba). Ambos extremos quedan incluidos.
 */
export const rangeBetween = <T>(items: T[], from: number, to: number): T[] => {
  const [start, end] = from < to ? [from, to] : [to, from]

  return items.slice(start, end + 1)
}
